import { useState } from "react";
import logoReq from "../assets/logo.png";

import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { post, termsConditions } from "../apis/apis";
import toastAlert from "./toastAlert";
import ResidencyDisclosure from "./ResidencyDisclosure";

const VerificationDialog = ({
  open,
  setOpen,
  needsVerification,
  fileId,
  compId,
  callbackData,
  logo,
  primaryColorComp,
  welcomeMessage,
  acknowledgementMessage,
  companyName,
  regionDisplayName,
}) => {
  const defaultWelcomeMessage = "Welcome to RequireSign";
  const defaultAcknowledgementMessage =
    "By proceeding, I agree to sign electronically, with my e-signature carrying legal validity.";
  const displayWelcomeMessage = welcomeMessage || defaultWelcomeMessage;
  const displayAcknowledgementMessage =
    acknowledgementMessage || defaultAcknowledgementMessage;
  const [agreed, setAgreed] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendOtp, setResendOtp] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [isResending, setIsResending] = useState(false);

  // ✅ Send OTP API Call
  const handleEmailChange = (e) => {
    if (otpSent) {
      setOtpSent(false);
      setOtp("");
      setResendOtp(false);
    }
    setEmail(e.target.value);
    setErrors((prev) => ({ ...prev, email: "" }));
  };

  // ✅ Handle Name Change - Always Editable
  const handleNameChange = (e) => {
    setName(e.target.value);
    setErrors((prev) => ({ ...prev, name: "" }));
  };
  const handleSendOtp = async () => {
    let newErrors = {};

    if (!agreed) newErrors.agreed = "You must agree to the terms & conditions";
    if (!name.trim()) newErrors.name = "Name is required";
    if (!email.includes("@")) newErrors.email = "Enter a valid email";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const checkEmailData = await post(
        "bulk_links/checkEmailExistforSpecificResponse",
        {
          email,
          bulk_link_id: fileId,
        }
      );

      if (checkEmailData.error) {
        toastAlert("error", "Can't submit response with the same email");
        setIsSubmitting(false);
        return;
      }

      const otpResponse = await post("user/account-verification", {
        email,
        company_id: compId,
      });

      if (otpResponse.error) {
        toastAlert("error", otpResponse.message);
      } else {
        callbackData({ name, email });
        localStorage.setItem("otp", JSON.stringify(otpResponse));
        setOtpSent(true);
        setResendOtp(true);
        toastAlert("success", "OTP sent successfully!");
      }
    } catch (error) {
      toastAlert("error", "Failed to send OTP. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    await handleSendOtp();
    setIsResending(false);
  };

  const handleVerifyOtp = async () => {
    const storedOtp = JSON.parse(localStorage.getItem("otp"))?.otp;

    if (!otp) {
      setOtpError("Enter OTP to verify!");
      return;
    }

    if (parseInt(storedOtp) !== parseInt(otp)) {
      setOtpError("Invalid OTP");
      return;
    }

    setOtpError("");
    callbackData({ name, email });
    setOpen(false);
    toastAlert("success", "OTP verified successfully!");
  };

  return (
    <Dialog
      open={open}
      // onClose={() => setOpen(false)}
      maxWidth="sm"
      fullScreen={window.innerWidth < 780 ? true : false}
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(255, 255, 255, 0.95)",
          },
        },
      }}
    >
      <DialogContent sx={{ textAlign: "center", p: 4 }}>
        {window.innerWidth < 780 && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <img
              src={logo === null || logo === undefined ? logoReq : logo} // Make sure 'logo' is defined
              alt="E-Sign Logo"
              style={{ width: "120px", height: "auto" }}
            />
          </Box>
        )}
        <DialogTitle sx={{ fontWeight: "bold", fontSize: 22 }}>
          {displayWelcomeMessage}
        </DialogTitle>

        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={(e) => {
                setAgreed(e.target.checked);
                setErrors((prev) => ({ ...prev, agreed: "" }));
              }}
            />
          }
          label={
            <Typography>
              {displayAcknowledgementMessage}{" "}
              <a href={`${termsConditions}`} target="_blank">
                Terms & Conditions
              </a>
            </Typography>
          }
        />
        <Typography
          variant="body2"
          sx={{ color: "rgba(0, 0, 0, 0.7)", textAlign: "left", mt: 0.5 }}
        >
          <ResidencyDisclosure
            companyName={companyName}
            regionDisplayName={regionDisplayName}
          />
        </Typography>
        {errors.agreed && (
          <Typography color="error" sx={{ fontSize: 12 }}>
            {errors.agreed}
          </Typography>
        )}

        <Typography sx={{ color: "#555", mb: 2, mt: 2 }}>
          Please verify your details before proceeding.
        </Typography>

        <Typography sx={{ textAlign: "left", mt: 1 }}>Name</Typography>
        <TextField
          fullWidth
          variant="outlined"
          margin="normal"
          value={name}
          onChange={handleNameChange}
          // onChange={(e) => {
          //   setName(e.target.value);
          //   setErrors((prev) => ({ ...prev, name: "" }));
          // }}
          error={!!errors.name}
          helperText={errors.name}
        />

        <Typography sx={{ textAlign: "left", mt: 1 }}>Email</Typography>
        <TextField
          fullWidth
          variant="outlined"
          margin="normal"
          value={email}
          // onChange={(e) => {
          //   setEmail(e.target.value);
          //   setErrors((prev) => ({ ...prev, email: "" }));
          // }}
          onChange={handleEmailChange}
          error={!!errors.email}
          helperText={errors.email}
        />

        {needsVerification && !otpSent && (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 2, boxShadow: "none" }}
            onClick={handleSendOtp}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Send OTP"}
          </Button>
        )}

        {otpSent && (
          <>
            <Typography sx={{ textAlign: "left", mt: 1 }}>Enter OTP</Typography>
            <TextField
              fullWidth
              variant="outlined"
              margin="normal"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              error={!!otpError}
              helperText={otpError}
            />

            <Button
              fullWidth
              variant="contained"
              color="success"
              sx={{ mt: 2 }}
              onClick={handleVerifyOtp}
            >
              Verify OTP
            </Button>

            {resendOtp && (
              <Typography
                sx={{ mt: 1, color: "blue", cursor: "pointer" }}
                onClick={handleResendOtp}
              >
                {isResending ? <CircularProgress size={20} /> : "Resend OTP"}
              </Typography>
            )}
          </>
        )}
        {!needsVerification && (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => {
              if (!name) {
                setErrors((prev) => ({ ...prev, name: "Name is required" }));
                return;
              }
              if (!email.includes("@")) {
                setErrors((prev) => ({
                  ...prev,
                  email: "Enter a valid email",
                }));
                return;
              }
              if (!agreed) {
                toastAlert("error", "You must agree to the terms & conditions");
                // setErrors((prev) => ({
                //   ...prev,
                //   agreed: "You must agree to the terms & conditions",
                // }));
                return;
              }
              callbackData({
                name,
                email,
              });
              setOpen(false);
            }}
          >
            Open
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VerificationDialog;
